/**
 * SecurityCard — compact tile on the Dashboard Overview, founder-approved
 * 2026-10-20 (option D). Reads the two cheapest security endpoints
 * (`threat-senders` + `blocking`) across a fixed 7-day window and tells
 * the merchant whether anything got through this week + whether blocking
 * is on. Links into Security · Summary.
 *
 * Not noisy — on a quiet week the card renders a one-line mint strip.
 * On a loud week it swaps for a coral strip with the sender count.
 *
 * The 7-day window is deliberate: it matches the Summary's default and
 * the hosted threat alert emails' recap period, so the merchant reads
 * the same number in both places.
 *
 * Dismiss (founder 2026-10-21): the coral attacked variant carries an
 * `X` button that hides the banner until a NEWER alert arrives. The
 * dismiss state is a per-viewer convenience stored in `localStorage` —
 * no server dismiss / acknowledge endpoint exists for threat alerts
 * today (grep `services/api` for `dismiss`/`acknowledge` turned up the
 * `dismissed_bot_actions` pattern only, which is per-agent-key, not
 * per-threat). We store the most recent `last_seen` across succeeded
 * senders as the fingerprint; a later probe window whose max
 * `last_seen` is strictly newer re-shows the banner.
 */

import { X } from 'lucide-react';
import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';

import type { Site } from '@sill/types';
import type { BlockingResponse, ThreatSendersListResponse, ThreatSenderSummary } from '@sill/types/api';

import type { SetView } from '../../app/view-router.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getBlocking, listThreatSenders } from '../../api/threats.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/** Per-site localStorage key: stores the max `last_seen` of the most
 *  recent batch of succeeded senders the merchant dismissed. */
function dismissKey(site_id: string): string {
  return `security-alert-dismissed:${site_id}`;
}

function readDismissedFingerprint(site_id: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(dismissKey(site_id));
  } catch {
    return null;
  }
}

function writeDismissedFingerprint(site_id: string, fingerprint: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(dismissKey(site_id), fingerprint);
  } catch {
    // storage-disabled browsers just re-show on next load — not a
    // surprise, same copy. Fail-soft.
  }
}

/**
 * Fingerprint for "the current alert" — the max `last_seen` across
 * every succeeded sender in the response. Returns null when nothing
 * got through (no alert to fingerprint). A dismiss stores this
 * fingerprint; a later probe window with a strictly newer fingerprint
 * means a NEW alert has arrived and the banner must re-show.
 *
 * `last_seen` is an ISO-8601 UTC timestamp (CLAUDE.md §3), so a
 * lexicographic comparison equals a chronological one.
 */
export function newestSucceededFingerprint(senders: readonly ThreatSenderSummary[]): string | null {
  let newest: string | null = null;
  for (const s of senders) {
    if (!s.any_succeeded) continue;
    if (newest === null || s.last_seen > newest) newest = s.last_seen;
  }
  return newest;
}

type RemoteState<T> = { kind: 'loading' } | { kind: 'error' } | { kind: 'loaded'; data: T };

/**
 * Date for `date_from` on a 7-day window. Sends `today - 6` so the
 * server-computed `[midnight(date_from), now)` diff stays ≤ 7 days
 * (same off-by-one as SecuritySummary — founder report 2026-10-20
 * item 4).
 */
function sevenDaysAgoIsoDate(): string {
  const d = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

export interface SecurityCardProps {
  readonly site: Site;
  readonly setView: SetView;
}

export function SecurityCard({ site, setView }: SecurityCardProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const [senders, setSenders] = useState<RemoteState<ThreatSendersListResponse>>({ kind: 'loading' });
  const [blocking, setBlocking] = useState<RemoteState<BlockingResponse>>({ kind: 'loading' });
  // Dismiss fingerprint is seeded from localStorage on mount + reseeded
  // when the active site changes, so switching sites never leaks one
  // site's dismiss into another's banner.
  const [dismissedFingerprint, setDismissedFingerprint] = useState<string | null>(
    () => readDismissedFingerprint(site.site_id),
  );
  useEffect(() => {
    setDismissedFingerprint(readDismissedFingerprint(site.site_id));
  }, [site.site_id]);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void Promise.allSettled([
      listThreatSenders(http, site.site_id, { date_from: sevenDaysAgoIsoDate() }),
      getBlocking(http, site.site_id),
    ]).then(([s, b]) => {
      if (cancelled) return;
      setSenders(s.status === 'fulfilled' ? { kind: 'loaded', data: s.value } : { kind: 'error' });
      setBlocking(b.status === 'fulfilled' ? { kind: 'loaded', data: b.value } : { kind: 'error' });
    });
    return () => { cancelled = true; };
  }, [http, site.site_id]);

  if (senders.kind === 'loading' && blocking.kind === 'loading') {
    // Keep the card out of the DOM while nothing is known — the
    // Dashboard already has a lot above the fold.
    return null;
  }

  if (senders.kind === 'error' && blocking.kind === 'error') {
    return null;
  }

  const sendersData = senders.kind === 'loaded' ? senders.data : null;
  const blockingData = blocking.kind === 'loaded' ? blocking.data : null;
  const senderList = Array.isArray(sendersData?.senders) ? sendersData.senders : [];
  // Founder report 2026-10-20 item 5: `.filter(any_succeeded)` only
  // counts senders on the FIRST PAGE. Blocked / handled senders sort
  // last, so a succeeded sender can disappear from the first page and
  // the "got through" count under-reports. Use the server's full-window
  // `senders_got_through`, which is independent of pagination.
  const succeededCount = typeof sendersData?.senders_got_through === 'number'
    ? sendersData.senders_got_through
    : senderList.filter((s) => s.any_succeeded).length;
  const anySucceeded = succeededCount > 0;
  const totalSenders = typeof sendersData?.total_count === 'number' ? sendersData.total_count : 0;
  // `requests_total` is the full-window sum (same reasoning as
  // `senders_got_through`); fall back to the page sum for old responses.
  const requestsCount = typeof sendersData?.requests_total === 'number'
    ? sendersData.requests_total
    : senderList.reduce((n, s) => n + (s.request_count ?? 0), 0);
  // Older HTTP fixtures in sibling test suites may answer /blocking with a
  // shape that omits `.blocking` or `.counts`; render defensively so the
  // card never throws from a sparse mock.
  const blockingOn = blockingData?.blocking?.mode === 'on';
  const liveBlocks = blockingData?.counts?.live_blocks ?? 0;

  const go = (): void => setView('security', { security_section: 'summary' });

  // Fingerprint of the current alert — max `last_seen` across succeeded
  // senders. Only relevant on the attacked branch; a dismiss stored for
  // the exact same fingerprint hides the banner until a strictly newer
  // one arrives.
  const fingerprint = newestSucceededFingerprint(senderList);
  const isDismissed =
    fingerprint !== null
    && dismissedFingerprint !== null
    && dismissedFingerprint >= fingerprint;

  // Plain function — the dismiss handler captures the current
  // fingerprint by closure. We can't lift this to `useCallback` without
  // reordering above the early returns (hooks must be unconditional),
  // and the ref-stability it would give us isn't needed: the button
  // doesn't memoise and isn't inside a hook dependency array.
  const onDismiss = (e: ReactMouseEvent): void => {
    // The dismiss button sits inside the clickable card (`<a>`); stop
    // the event so the card itself doesn't navigate on the same click.
    e.preventDefault();
    e.stopPropagation();
    if (fingerprint === null) return;
    writeDismissedFingerprint(site.site_id, fingerprint);
    setDismissedFingerprint(fingerprint);
  };

  if (anySucceeded && !isDismissed) {
    const label = succeededCount === 1
      ? '1 sender got a success response'
      : `${succeededCount.toLocaleString('en-US')} senders got a success response`;
    return (
      <a
        href="#security"
        onClick={(e) => { e.preventDefault(); go(); }}
        data-testid="dashboard-security-card-attacked"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto auto',
          gap: 16,
          alignItems: 'center',
          padding: '16px 18px',
          background: C.surface,
          border: `1px solid ${C.coralFg}`,
          borderRadius: 3,
          textDecoration: 'none',
          color: C.text,
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.coralFg, textTransform: 'uppercase' }}>
            Security · last 7 days
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 16, fontWeight: 600 }}>
            {label} · {requestsCount.toLocaleString('en-US')} attack requests from {totalSenders.toLocaleString('en-US')} senders
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
            {blockingOn
              ? `Blocking on · ${liveBlocks.toLocaleString('en-US')} addresses blocked`
              : `Blocking off · ${liveBlocks.toLocaleString('en-US')} addresses saved`}
          </div>
        </div>
        <span
          style={{
            font: `600 13px ${sansFont}`,
            padding: '10px 14px',
            // Same look as the primary Button (lime on ink) in both themes.
            background: C.lime,
            color: C.ink,
            borderRadius: 3,
          }}
        >
          See what happened
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss alert"
          data-testid="dashboard-security-card-dismiss"
          style={{
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: C.muted,
            cursor: 'pointer',
            padding: 0,
            borderRadius: 2,
          }}
        >
          <X size={14} />
        </button>
      </a>
    );
  }

  // Quiet week.
  return (
    <a
      href="#security"
      onClick={(e) => { e.preventDefault(); go(); }}
      data-testid="dashboard-security-card-quiet"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 16,
        alignItems: 'center',
        padding: '14px 18px',
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        textDecoration: 'none',
        color: C.text,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={C.mintFg} strokeWidth={2} aria-hidden="true">
          <path d="M5 12l5 5L20 7" />
        </svg>
        <div style={{ fontFamily: sansFont, fontSize: 14 }}>
          <span style={{ fontWeight: 600 }}>Nothing got through this week.</span>{' '}
          <span style={{ color: C.body }}>
            {totalSenders.toLocaleString('en-US')} {totalSenders === 1 ? 'sender' : 'senders'} on paths that don’t exist
            {blockingOn ? ' · blocking on' : ' · blocking off'}
          </span>
        </div>
      </div>
      <span style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg }}>Security →</span>
    </a>
  );
}
